import type {
  CodeableConcept,
  HumanName,
  Patient,
  Resource,
} from '@medplum/fhirtypes';

/**
 * Semantic accessors: cross-type answers to "what is this resource's name /
 * date". Mixed-type surfaces (tables, chips, search results) need one
 * consistent projection per concept; the per-type dispatch lives here once.
 */

export function formatHumanName(name: HumanName | undefined): string {
  if (!name) return '';
  return (
    name.text ??
    [name.prefix?.join(' '), name.given?.join(' '), name.family]
      .filter(Boolean)
      .join(' ')
  );
}

/**
 * Index of the identifier typed as a Medical Record Number (v2-0203 `MR`).
 * A cell labeled "MRN" must never fall back to identifier[0] — integration
 * bookkeeping identifiers (internal ids) land there and would masquerade as
 * the chart number. No MR-typed identifier → an index past the array, so
 * Field renders its honest not-recorded state.
 */
export function patientMrnIdentifierIndex(data: Patient): number {
  const identifiers = data.identifier ?? [];
  const index = identifiers.findIndex((identifier) =>
    identifier.type?.coding?.some((coding) => coding.code === 'MR'),
  );
  return index >= 0 ? index : identifiers.length;
}

/** CodeableConcept display: explicit text wins, else the first coding's display. */
export function conceptText(
  concept: CodeableConcept | undefined,
): string | undefined {
  return concept?.text ?? concept?.coding?.[0]?.display;
}

function firstHumanName(
  resource: Resource & { name?: HumanName[] },
): string | undefined {
  const assembled = formatHumanName(resource.name?.[0]);
  return assembled || undefined;
}

/** Display name for any resource: human name, org/team name, or coded concept text. */
export function resourceDisplayName(resource: Resource): string {
  switch (resource.resourceType) {
    case 'Patient':
    case 'Practitioner':
    case 'RelatedPerson':
    case 'Person':
      return firstHumanName(resource) ?? resource.resourceType;
    case 'Organization':
    case 'CareTeam':
    case 'Location':
      return resource.name ?? resource.resourceType;
    case 'PractitionerRole':
      return (
        resource.code?.[0]?.text ??
        resource.specialty?.[0]?.text ??
        resource.resourceType
      );
    case 'Observation':
    case 'Condition':
    case 'DiagnosticReport':
    case 'Procedure':
    case 'AllergyIntolerance':
    case 'Immunization':
    case 'Medication':
      return (
        ('code' in resource ? conceptText(resource.code) : undefined) ??
        ('vaccineCode' in resource
          ? conceptText(resource.vaccineCode)
          : undefined) ??
        resource.resourceType
      );
    case 'MedicationRequest':
    case 'MedicationStatement':
      return (
        conceptText(resource.medicationCodeableConcept) ??
        resource.medicationReference?.display ??
        resource.resourceType
      );
    case 'DocumentReference':
      return (
        resource.description ?? resource.type?.text ?? resource.resourceType
      );
    case 'Goal':
      // A goal's display label lives on `description`, not `code`.
      return conceptText(resource.description) ?? resource.resourceType;
    default:
      return resource.resourceType;
  }
}

/** Clinically-relevant date for any resource, ISO string. Falls back to meta.lastUpdated. */
export function resourceDate(resource: Resource): string | undefined {
  switch (resource.resourceType) {
    case 'Observation':
    case 'DiagnosticReport':
      return resource.effectiveDateTime ?? resource.issued;
    case 'Condition':
      return resource.onsetDateTime ?? resource.recordedDate;
    case 'Procedure':
      return resource.performedDateTime ?? resource.performedPeriod?.start;
    case 'AllergyIntolerance':
      return resource.onsetDateTime ?? resource.recordedDate;
    case 'MedicationRequest':
      return resource.authoredOn;
    case 'MedicationStatement':
      return resource.effectiveDateTime ?? resource.dateAsserted;
    case 'Immunization':
      return resource.occurrenceDateTime ?? resource.recorded;
    case 'DocumentReference':
      return resource.date;
    case 'Goal':
      return resource.startDate ?? resource.statusDate;
    case 'Encounter':
      return resource.period?.start;
    default:
      return resource.meta?.lastUpdated;
  }
}
